import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Plus, Shield, Trash2, Users } from 'lucide-react';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Checkbox } from '~/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Skeleton } from '~/components/ui/skeleton';
import { Textarea } from '~/components/ui/textarea';
import { PERMISSION_LABELS, PERMISSION_GROUPS } from './permission-config';

type RoleFormData = {
    name: string;
    description: string;
    permissions: string[];
};

const RolesManagement = () => {
    const navigate = useNavigate();
    const utils = trpc.useUtils();

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingRole, setEditingRole] = useState<{ id: string; data: RoleFormData } | null>(null);
    const [deleteConfirmRole, setDeleteConfirmRole] = useState<{ id: string; name: string } | null>(null);

    // Form state for create/edit
    const [formData, setFormData] = useState<RoleFormData>({
        name: '',
        description: '',
        permissions: [],
    });

    // Queries
    const { data: roles, isLoading: rolesLoading } = trpc.permissions.getRoles.useQuery();
    const { data: allPermissions } = trpc.permissions.getAllPermissions.useQuery();

    // Mutations
    const createRoleMutation = trpc.permissions.createRole.useMutation({
        onSuccess: () => {
            toast.success('Rolle erstellt');
            utils.permissions.getRoles.invalidate();
            setIsCreateDialogOpen(false);
            resetForm();
        },
        onError: (error) => {
            toast.error('Fehler beim Erstellen der Rolle', { description: error.message });
        },
    });

    const updateRoleMutation = trpc.permissions.updateRole.useMutation({
        onSuccess: () => {
            toast.success('Rolle aktualisiert');
            utils.permissions.getRoles.invalidate();
            setEditingRole(null);
            resetForm();
        },
        onError: (error) => {
            toast.error('Fehler beim Aktualisieren der Rolle', { description: error.message });
        },
    });

    const deleteRoleMutation = trpc.permissions.deleteRole.useMutation({
        onSuccess: () => {
            toast.success('Rolle gelöscht');
            utils.permissions.getRoles.invalidate();
            setDeleteConfirmRole(null);
        },
        onError: (error) => {
            toast.error('Fehler beim Löschen der Rolle', { description: error.message });
        },
    });

    const resetForm = () => {
        setFormData({ name: '', description: '', permissions: [] });
    };

    const openEditDialog = (role: { id: string; name: string; description: string | null; permissions: string[] }) => {
        setFormData({
            name: role.name,
            description: role.description || '',
            permissions: role.permissions,
        });
        setEditingRole({ id: role.id, data: { name: role.name, description: role.description || '', permissions: role.permissions } });
    };

    const handlePermissionToggle = (permission: string) => {
        setFormData((prev) => ({
            ...prev,
            permissions: prev.permissions.includes(permission)
                ? prev.permissions.filter((p) => p !== permission)
                : [...prev.permissions, permission],
        }));
    };

    const handleSubmit = () => {
        if (editingRole) {
            updateRoleMutation.mutate({
                roleId: editingRole.id,
                name: formData.name,
                description: formData.description || undefined,
                permissions: formData.permissions as any[],
            });
        } else {
            createRoleMutation.mutate({
                name: formData.name,
                description: formData.description || undefined,
                permissions: formData.permissions as any[],
            });
        }
    };

    const isFormValid = formData.name.trim().length > 0;
    const isSaving = createRoleMutation.isPending || updateRoleMutation.isPending;

    return (
        <Content>
            <div className='container mx-auto py-10'>
                {/* Header */}
                <div className='flex items-center gap-4 mb-6'>
                    <Button variant='ghost' size='icon' onClick={() => navigate('/permissions')}>
                        <ArrowLeft className='h-5 w-5' />
                    </Button>
                    <div>
                        <h1 className='text-3xl font-bold'>Rollen</h1>
                        <p className='text-muted-foreground'>
                            Erstellen und verwalten Sie Rollen mit Berechtigungsgruppen
                        </p>
                    </div>
                </div>

                {/* Create button */}
                <div className='mb-6'>
                    <Button onClick={() => { resetForm(); setIsCreateDialogOpen(true); }}>
                        <Plus className='h-4 w-4 mr-2' />
                        Neue Rolle erstellen
                    </Button>
                </div>

                {/* Roles list */}
                {rolesLoading ? (
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                        {[1, 2, 3].map((i) => (
                            <Card key={i}>
                                <CardHeader>
                                    <Skeleton className='h-6 w-32' />
                                    <Skeleton className='h-4 w-48' />
                                </CardHeader>
                                <CardContent>
                                    <Skeleton className='h-4 w-full mb-2' />
                                    <Skeleton className='h-4 w-3/4' />
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : roles && roles.length > 0 ? (
                    <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
                        {roles.map((role) => (
                            <Card key={role.id}>
                                <CardHeader>
                                    <div className='flex items-start justify-between'>
                                        <div className='flex items-center gap-2'>
                                            <Shield className='h-5 w-5 text-primary' />
                                            <CardTitle className='text-lg'>{role.name}</CardTitle>
                                        </div>
                                        <div className='flex gap-1'>
                                            <Button
                                                variant='ghost'
                                                size='icon'
                                                onClick={() => openEditDialog(role)}
                                            >
                                                <Pencil className='h-4 w-4' />
                                            </Button>
                                            <Button
                                                variant='ghost'
                                                size='icon'
                                                onClick={() => setDeleteConfirmRole({ id: role.id, name: role.name })}
                                            >
                                                <Trash2 className='h-4 w-4 text-destructive' />
                                            </Button>
                                        </div>
                                    </div>
                                    {role.description && (
                                        <CardDescription>{role.description}</CardDescription>
                                    )}
                                </CardHeader>
                                <CardContent>
                                    <div className='flex items-center gap-2 text-sm text-muted-foreground mb-3'>
                                        <Users className='h-4 w-4' />
                                        <span>{role.userCount} Benutzer</span>
                                    </div>
                                    <div className='flex flex-wrap gap-1'>
                                        {role.permissions.slice(0, 5).map((permission) => (
                                            <Badge key={permission} variant='secondary' className='text-xs'>
                                                {PERMISSION_LABELS[permission] || permission}
                                            </Badge>
                                        ))}
                                        {role.permissions.length > 5 && (
                                            <Badge variant='outline' className='text-xs'>
                                                +{role.permissions.length - 5} weitere
                                            </Badge>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card>
                        <CardContent className='py-10 text-center text-muted-foreground'>
                            <Shield className='h-12 w-12 mx-auto mb-4 opacity-50' />
                            <p>Noch keine Rollen erstellt.</p>
                            <p className='text-sm'>Erstellen Sie Ihre erste Rolle, um Berechtigungen zu gruppieren.</p>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog
                open={isCreateDialogOpen || !!editingRole}
                onOpenChange={(open) => {
                    if (!open) {
                        setIsCreateDialogOpen(false);
                        setEditingRole(null);
                        resetForm();
                    }
                }}
            >
                <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
                    <DialogHeader>
                        <DialogTitle>
                            {editingRole ? 'Rolle bearbeiten' : 'Neue Rolle erstellen'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingRole
                                ? 'Bearbeiten Sie die Rolle und ihre Berechtigungen.'
                                : 'Erstellen Sie eine neue Rolle mit einer Gruppe von Berechtigungen.'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className='space-y-6 py-4'>
                        {/* Name */}
                        <div className='space-y-2'>
                            <Label htmlFor='name'>Name *</Label>
                            <Input
                                id='name'
                                value={formData.name}
                                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                                placeholder='z.B. Arzt, MFA, Rezeption'
                            />
                        </div>

                        {/* Description */}
                        <div className='space-y-2'>
                            <Label htmlFor='description'>Beschreibung</Label>
                            <Textarea
                                id='description'
                                value={formData.description}
                                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                                placeholder='Optionale Beschreibung der Rolle'
                                rows={2}
                            />
                        </div>

                        {/* Permissions */}
                        <div className='space-y-4'>
                            <Label>Berechtigungen</Label>
                            {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => (
                                <div key={group} className='border rounded-lg p-4'>
                                    <h4 className='font-medium mb-3'>{group}</h4>
                                    <div className='space-y-2'>
                                        {permissions
                                            .filter((p) => allPermissions?.includes(p as any))
                                            .map((permission) => (
                                                <div key={permission} className='flex items-center gap-2'>
                                                    <Checkbox
                                                        id={permission}
                                                        checked={formData.permissions.includes(permission)}
                                                        onCheckedChange={() => handlePermissionToggle(permission)}
                                                    />
                                                    <label
                                                        htmlFor={permission}
                                                        className='text-sm cursor-pointer'
                                                    >
                                                        {PERMISSION_LABELS[permission] || permission}
                                                    </label>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant='outline'
                            onClick={() => {
                                setIsCreateDialogOpen(false);
                                setEditingRole(null);
                                resetForm();
                            }}
                        >
                            Abbrechen
                        </Button>
                        <Button onClick={handleSubmit} disabled={!isFormValid || isSaving}>
                            {isSaving ? 'Speichern...' : editingRole ? 'Speichern' : 'Erstellen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={!!deleteConfirmRole} onOpenChange={() => setDeleteConfirmRole(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rolle löschen</DialogTitle>
                        <DialogDescription>
                            Sind Sie sicher, dass Sie die Rolle &quot;{deleteConfirmRole?.name}&quot; löschen möchten?
                            Alle Benutzer mit dieser Rolle verlieren die zugehörigen Berechtigungen.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => setDeleteConfirmRole(null)}>
                            Abbrechen
                        </Button>
                        <Button
                            variant='destructive'
                            onClick={() => deleteConfirmRole && deleteRoleMutation.mutate({ roleId: deleteConfirmRole.id })}
                            disabled={deleteRoleMutation.isPending}
                        >
                            {deleteRoleMutation.isPending ? 'Löschen...' : 'Löschen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Content>
    );
};

export default RolesManagement;
